import { Injectable } from '@angular/core';
import {
  ConnectDeclineReason,
  IConnectContact,
} from '@epicuro-next/data/connect';
import { SignalRService } from '@epicuro-next/data/signalr';
import { ConnectionStatus } from '@epicuro-next/models/users';
import {
  ConnectMessageType,
  ConnectNotificationType,
} from '@epicuro-next/platform/configuration';
import * as fromSignalrState from '@epicuro-next/state/signalr-state';
import { selectUserId } from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import * as actions from '../actions';
import { selectConnectIsEnabled, selectContact } from '../selectors';

@Injectable()
export class ConnectSignalrEffects {
  constructor(
    private actions$: Actions,
    private signalrService: SignalRService,
    private store: Store,
  ) {}

  public initSocketConnectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSignalrState.initSignalrConnectionSuccess),
      concatLatestFrom(() => [this.store.pipe(select(selectConnectIsEnabled))]),
      filter(([, connectIsEnabled]) => connectIsEnabled),
      switchMap(() => [
        actions.setUserConnectionStatus({
          status: ConnectionStatus.AVAILABLE,
        }),
      ]),
    ),
  );

  public setContactConnectionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSignalrState.initSignalrConnectionSuccess),
      concatLatestFrom(() => [
        this.store.pipe(select(selectUserId)),
        this.store.pipe(select(selectConnectIsEnabled)),
      ]),
      filter(([, , connectIsEnabled]) => connectIsEnabled),
      switchMap(([, currentUserId]) =>
        this.signalrService
          .messages<[string, string, ConnectionStatus]>(
            ConnectMessageType.OnConnectionStatusChange,
          )
          .pipe(
            filter(([, userId]) => currentUserId !== userId),
            map(([, userId, status]) =>
              actions.setContactConnectionStatus({
                userId,
                status,
              }),
            ),
          ),
      ),
    ),
  );

  public incomingCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSignalrState.initSignalrConnectionSuccess),
      switchMap(() =>
        this.signalrService.messages<[string, string, string]>(
          ConnectMessageType.OnInitiateVideoCall,
        ),
      ),
      switchMap(([, userId, roomName]) =>
        this.store.select(selectContact(userId)).pipe(
          map((contact: IConnectContact) =>
            actions.setIncomingCall({
              incomingCall: {
                contact,
                roomName,
              },
            }),
          ),
          take(1),
        ),
      ),
    ),
  );

  public listenSocketCallDeclineMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.listenSocketCallDeclineMessage),
      switchMap(() =>
        this.signalrService.messages<
          [string, string, string, ConnectDeclineReason]
        >(ConnectMessageType.OnDeclineVideoCall),
      ),
      withLatestFrom(this.store.select(selectUserId)),
      switchMap(([[, userId, , reason], currentUserId]) => [
        userId === currentUserId
          ? actions.setIncomingCall({ incomingCall: null })
          : actions.setDeclineReason({ reason }),
        fromSignalrState.closeSignalrChannel({
          message: ConnectNotificationType.DeclineVideoCall,
        }),
      ]),
    ),
  );

  public listenSocketOnCancelVideoCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.listenSocketOnCancelVideoCall),
      switchMap(() =>
        this.signalrService.messages(ConnectMessageType.OnCancelVideoCall),
      ),
      switchMap(() => [
        actions.setIncomingCall({ incomingCall: null }),
        actions.stopListenSocketCancelVideoCall(),
      ]),
    ),
  );

  public stopListenSocketCancelVideoCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.stopListenSocketCancelVideoCall),
      map(() =>
        fromSignalrState.closeSignalrChannel({
          message: ConnectNotificationType.CancelVideoCall,
        }),
      ),
    ),
  );

  public loadUserContactsState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromSignalrState.signalrReconnected),
      map(() => actions.loadUserContactsState()),
    ),
  );
}
